import React, { useEffect, useRef, useState } from 'react';
import {
  Grid,
  IconButton,
  Slider,
  Tooltip,
  Typography,
  Box,
  Button,
  Menu,
  MenuItem
} from '@mui/material';
import { PlayArrow, Pause, FileDownloadOutlined } from '@mui/icons-material';

import { Colors, isLightTheme } from '../config/default';
import { formatDuration } from '../constants/voiceNoteUtils';
import { voiceNote as voiceNoteText } from '../constants/appConstants';

/**
 * The transport for a voice note: play/pause, a seek bar, the elapsed time and
 * a slot on the right for whatever action the caller owns (delete, discard).
 *
 * It is a themed control rather than `<audio controls>`, which renders the
 * BROWSER's player — in Chrome a white pill with its own overflow menu, which
 * ignores the app's palette completely and reads as a foreign object dropped
 * into the activity feed. The <audio> element is still what plays the sound; it
 * is just no longer what is on screen.
 *
 * Used by both halves of the feature — the recorder's preview and the posted
 * comment's player — so a recording looks the same before and after it is sent.
 *
 * Playback speed and download are here because dropping <audio controls> took
 * away the browser's own menu, and both are things a reviewer genuinely does
 * with a voice note: run a slow talker at 2x, or keep the recording alongside
 * the invoice. Losing them was a regression, not a simplification.
 */

// What Chrome's own menu offers, trimmed to the ends of the range people use.
const PLAYBACK_RATES = [0.5, 1, 1.5, 2];
const VoiceNoteTrack = ({ src, durationSeconds, fileName, children }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [metaDuration, setMetaDuration] = useState(0);
  const [rate, setRate] = useState(1);
  const [rateAnchor, setRateAnchor] = useState(null);

  // A MediaRecorder webm is written without a duration in its header, so the
  // element reports Infinity for one and a seek bar built on that has no end.
  // The length recorded at capture time is the reliable answer; the element's
  // own duration is only the fallback, for an audio file attached some other
  // way.
  const recorded = Number(durationSeconds);
  const total = Number.isFinite(recorded) && recorded > 0 ? recorded : metaDuration;

  // Stop the clock at the end of the bar: a webm's reported time can run a
  // fraction past the length we are drawing against.
  const position = total ? Math.min(elapsed, total) : 0;

  const surface = isLightTheme() ? Colors.BG_BLACK : Colors.NAV_BLACK;

  // Leaving the feed (or the drawer) while a note is playing must not leave it
  // playing out of sight — the element is detached, not stopped, on unmount.
  // The element is captured here, at mount, rather than read from the ref in
  // the cleanup: React detaches host refs as it tears the tree down, so by then
  // there may be nothing left to call pause on.
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio) audio.pause();
    };
  }, []);

  const togglePlay = (e) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      // Autoplay policies and a revoked blob URL both reject here; the state is
      // driven off the element's own events, so a refusal simply leaves it
      // showing Play rather than lying about playing.
      const started = audio.play();
      if (started && started.catch) started.catch(() => {});
    } else {
      audio.pause();
    }
  };

  // Set on the element rather than passed as a prop: playbackRate is a property
  // with no attribute, so React will not write it for us, and a rate chosen
  // while paused has to survive until the next play.
  const chooseRate = (value) => {
    setRate(value);
    setRateAnchor(null);
    if (audioRef.current) audioRef.current.playbackRate = value;
  };

  // The same anchor click the documents table uses for a signed URL. `download`
  // is honoured outright for the recorder's blob: preview; for a cross-origin S3
  // URL the browser ignores the attribute and the Content-Disposition the
  // bucket sends is what names the file.
  const handleDownload = (e) => {
    e.stopPropagation();
    if (!src) return;

    const link = document.createElement('a');
    link.href = src;
    link.setAttribute('download', fileName || 'voice-note.webm');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSeek = (e, value) => {
    const audio = audioRef.current;
    if (!audio || !total) return;
    audio.currentTime = value;
    setElapsed(value);
  };

  return (
    <Grid
      container
      alignItems="center"
      wrap="nowrap"
      sx={{
        backgroundColor: surface,
        padding: '4px 10px',
        borderRadius: '10px',
        gap: '0.6em'
      }}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => {
          if (Number.isFinite(e.target.duration)) setMetaDuration(e.target.duration);
        }}
        onTimeUpdate={(e) => setElapsed(e.target.currentTime)}
        onPlay={(e) => {
          // Chrome resets playbackRate to 1 when a source reloads, so the
          // chosen speed is re-asserted at the start of every play.
          e.target.playbackRate = rate;
          setPlaying(true);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setElapsed(0);
          if (audioRef.current) audioRef.current.currentTime = 0;
        }}
      />

      <Tooltip title={playing ? voiceNoteText.PAUSE : voiceNoteText.PLAY}>
        <IconButton
          onClick={togglePlay}
          aria-label={playing ? 'pause-voice-note' : 'play-voice-note'}
          sx={{
            width: '1.9rem',
            height: '1.9rem',
            backgroundColor: Colors.TOURQUISE,
            color: Colors.WHITE,
            '&:hover': { backgroundColor: Colors.LIGHT_TOURQUISE }
          }}>
          {playing ? <Pause sx={{ fontSize: '1.1rem' }} /> : <PlayArrow sx={{ fontSize: '1.1rem' }} />}
        </IconButton>
      </Tooltip>

      {/* minWidth:0 so the bar gives way instead of pushing the time and the
          delete button out of a narrow reply column. */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
        <Slider
          size="small"
          min={0}
          max={total || 1}
          step={0.1}
          value={position}
          onChange={handleSeek}
          disabled={!total}
          aria-label="voice-note-position"
          sx={{
            color: Colors.TOURQUISE,
            height: '3px',
            padding: '11px 0',
            '& .MuiSlider-rail': { backgroundColor: Colors.DEEPGREY, opacity: 1 },
            '& .MuiSlider-thumb': {
              width: '0.6rem',
              height: '0.6rem',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: `0 0 0 0.35rem ${Colors.TOURQUISE}33`
              },
              '&.Mui-active': { boxShadow: `0 0 0 0.5rem ${Colors.TOURQUISE}33` }
            },
            '&.Mui-disabled': { color: Colors.DEEPGREY }
          }}
        />
      </Box>

      {/* Tabular figures: without them the whole row twitches sideways once a
          second as the digits change width. */}
      <Typography
        variant="body2"
        sx={{
          color: Colors.LIGHTGREY,
          fontSize: '0.75rem',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums'
        }}>
        {`${formatDuration(position)} / ${formatDuration(total)}`}
      </Typography>

      <Tooltip title={voiceNoteText.PLAYBACK_SPEED}>
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setRateAnchor(e.currentTarget);
          }}
          aria-label="voice-note-speed"
          sx={{
            minWidth: 'auto',
            padding: '0 0.35em',
            color: rate === 1 ? Colors.LIGHTGREY : Colors.TOURQUISE,
            fontSize: '0.72rem',
            textTransform: 'none',
            fontVariantNumeric: 'tabular-nums',
            '&:hover': { backgroundColor: 'transparent', color: Colors.TOURQUISE }
          }}>
          {`${rate}x`}
        </Button>
      </Tooltip>

      <Menu
        anchorEl={rateAnchor}
        open={!!rateAnchor}
        onClose={() => setRateAnchor(null)}
        PaperProps={{ sx: { backgroundColor: surface, color: Colors.WHITE } }}>
        {PLAYBACK_RATES.map((value) => (
          <MenuItem
            key={value}
            dense
            selected={value === rate}
            onClick={() => chooseRate(value)}
            sx={{ fontSize: '0.8rem', color: value === rate ? Colors.TOURQUISE : Colors.WHITE }}>
            {`${value}x`}
          </MenuItem>
        ))}
      </Menu>

      <Tooltip title={voiceNoteText.DOWNLOAD}>
        <IconButton
          size="small"
          onClick={handleDownload}
          aria-label="download-voice-note"
          sx={{ color: Colors.LIGHTGREY, '&:hover': { color: Colors.WHITE } }}>
          <FileDownloadOutlined sx={{ fontSize: '1.1rem' }} />
        </IconButton>
      </Tooltip>

      {children}
    </Grid>
  );
};

export default VoiceNoteTrack;
